import { Checkbox, Input, Table } from 'react-daisyui';
import { useEffect, useMemo, useState } from 'react';
import type { User, Role, UserFields } from '@types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faEllipsisV,
  faMagnifyingGlass,
} from '@fortawesome/free-solid-svg-icons';
import { SortArrow } from '@/components/SortArrow/SortArrow';
import { MenuAction, Sort } from '@/types';
import { OSPagination } from '@/components/Pagination/Pagination';
import { DashboardCard } from '@/components/DashboardCard';
import { getPageRecords } from '@/utils/inmemory-pagination';
import './TableUsers.css';
import TableDropDown from '@/components/TableDropDown/TableDropDown';
import { RoleBadge } from './RoleBadge';

interface Props {
  users: User[];
  // eslint-disable-next-line react/no-unused-prop-types
  test?: boolean;
  actionsForAdmins: MenuAction[];
  actionsForClients: MenuAction[];
  actionsforSuperAdminOrInvestor: MenuAction[];
  actionsForDisabled: MenuAction[];
}
function TableUsers({
  users = [],
  actionsForAdmins,
  actionsForClients,
  actionsforSuperAdminOrInvestor,
  actionsForDisabled,
}: Props) {
  const [sort, setSort] = useState<Sort>({ field: 'last_name', asc: true });
  const [searchText, setSearchText] = useState<string>('');
  const [selectedPage, setSelectedPage] = useState<number>(1);
  const [showClients, setShowClients] = useState<boolean>(false);
  const [recordsPerPage, setRecordsPerPage] = useState<number>(10);
  const sortUsers = (): User[] =>
    users
      ?.sort((c1, c2) => {
        const obj1 =
          sort.field === 'clients'
            ? c1[sort.field]?.length || 0
            : c1[sort.field as UserFields];
        const obj2 =
          sort.field === 'clients'
            ? c2[sort.field]?.length || 0
            : c2[sort.field as UserFields];
        if (obj1 && obj2 && obj1 > obj2) return sort.asc ? 1 : -1;
        return sort.asc ? -1 : 1;
      })
      .sort((a, b) => {
        if (a.enabled === b.enabled) return 0;
        return a.enabled ? -1 : 1;
      })
      .filter((user) =>
        `${user.last_name?.toLowerCase()} ${user.first_name?.toLowerCase()}`.match(
          searchText.toLowerCase()
        )
      )
      .filter((user) => {
        if (!showClients) {
          return user.role !== 'client';
        }

        return true;
      });

  const sortBy = (fieldName: string) => {
    if (fieldName === sort.field) {
      setSort({ field: sort.field, asc: !sort.asc });
    } else {
      setSort({ field: fieldName, asc: true });
    }
  };

  const records = useMemo(
    () => sortUsers(),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [sort, selectedPage, searchText, users, showClients]
  );

  useEffect(() => {
    setSelectedPage(1);
  }, [sort, recordsPerPage, searchText]);

  const getActionsFromRole = (user: User) => {
    switch (user.role) {
      case 'client':
        return actionsForClients;
      case 'admin':
        if (user.enabled) {
          return actionsForAdmins;
        }
        return actionsForDisabled;

      case 'investor':
      case 'superadmin':
        return actionsforSuperAdminOrInvestor;
      default:
        return [];
    }
  };

  const getRoleDescription = (user: User) => {
    if (!user.enabled) {
      return `${user.role} - Archived` as Role;
    }
    if (user.role === 'investor') {
      return 'Third Party' as Role;
    }
    return user.role;
  };

  return (
    <DashboardCard className='p-4' cardBodyclassName='p-0'>
      <div className='mt-3 mb-1'>
        <div className='relative text-gray-600 focus-within:text-gray-400'>
          <span className='absolute inset-y-0 left-[260px] flex items-center pl-2'>
            <button
              type='submit'
              className='p-1 focus:outline-none focus:shadow-outline'
            >
              <FontAwesomeIcon icon={faMagnifyingGlass} />
            </button>
          </span>
          <Input
            data-testid='users-search-input'
            className='border-accent w-[300px]'
            placeholder='Search Users'
            size='sm'
            defaultValue={searchText}
            onChange={(event) => setSearchText(event.target.value)}
          />
          <span className='ml-10 text-white'>Show clients</span>
          <Checkbox
            checked={showClients}
            data-testid='showClients'
            className='border-accent relative top-[7px] ml-2'
            onChange={() => setShowClients(!showClients)}
          />
        </div>
      </div>
      <Table zebra data-testid='users-list' id='table-users'>
        <Table.Head data-testid='user-name-sorting'>
          <div
            role='button'
            tabIndex={0}
            onClick={() => sortBy('first_name')}
            className='cursor-pointer'
          >
            <span className='text-base font-bold capitalize'>
              First Name
              <SortArrow sort={sort} fieldName='first_name' />
            </span>
          </div>
          <div
            role='button'
            tabIndex={0}
            onClick={() => sortBy('last_name')}
            className='cursor-pointer'
          >
            <span className='text-base font-bold capitalize'>
              Last Name
              <SortArrow sort={sort} fieldName='last_name' />
            </span>
          </div>
          <div
            role='button'
            tabIndex={0}
            onClick={() => sortBy('email_address')}
            className='cursor-pointer'
          >
            <span className='text-base font-bold capitalize'>
              Email
              <SortArrow sort={sort} fieldName='email_address' />
            </span>
          </div>
          <div
            role='button'
            tabIndex={0}
            onClick={() => sortBy('role')}
            className='cursor-pointer'
          >
            <div className='text-base font-bold capitalize text-center'>
              Role
              <SortArrow sort={sort} fieldName='role' />
            </div>
          </div>
          <div>
            <div className='text-base font-bold capitalize text-center'>
              #Clients
            </div>
          </div>
          <span />
        </Table.Head>
        {records.length > 0 && (
          <Table.Body className='text-sm'>
            {(
              getPageRecords(records, selectedPage, recordsPerPage) as User[]
            ).map((user) => (
              <Table.Row
                key={user.id}
                data-testid='user-list-item'
                hover={user.role !== 'client'}
                className={user.role !== 'client' ? 'cursor-pointer' : ''}
              >
                <div
                  role='button'
                  tabIndex={0}
                  className={user.enabled ? '' : 'line-through text-gray-500'}
                  onClick={() =>
                    user.role !== 'client'
                      ? actionsForAdmins[0].action(user)
                      : null
                  }
                >
                  {user?.first_name}
                </div>
                <div
                  role='button'
                  tabIndex={0}
                  onClick={() =>
                    user.role !== 'client'
                      ? actionsForAdmins[0].action(user)
                      : null
                  }
                  className={user.enabled ? '' : 'line-through text-gray-500'}
                >
                  {user?.last_name}
                </div>
                <div
                  role='button'
                  tabIndex={0}
                  onClick={() =>
                    user.role !== 'client'
                      ? actionsForAdmins[0].action(user)
                      : null
                  }
                  className={user.enabled ? '' : 'line-through text-gray-500'}
                >
                  {user?.email_address}
                </div>
                <div
                  role='button'
                  tabIndex={0}
                  onClick={() =>
                    user.role !== 'client'
                      ? actionsForAdmins[0].action(user)
                      : null
                  }
                  className='text-center'
                >
                  <RoleBadge role={getRoleDescription(user)} />
                </div>
                <div
                  role='button'
                  tabIndex={0}
                  onClick={() =>
                    user.role !== 'client'
                      ? actionsForAdmins[0].action(user)
                      : null
                  }
                  className={
                    user.enabled
                      ? 'text-center'
                      : 'text-center line-through text-gray-500'
                  }
                >
                  <div
                    className={user.enabled ? '' : 'line-through text-gray-500'}
                  >
                    {user.role === 'admin' ? user.clients?.length : 'N/A'}
                  </div>
                </div>
                <div>
                  {!(user.role === 'client' && !user.hubspot_id) &&
                  !!user.role ? (
                    <TableDropDown
                      icon={faEllipsisV}
                      options={getActionsFromRole(user)}
                      testId='edit-user-button'
                      itemToPerformActionOn={user}
                      id={user.id.toString()}
                    />
                  ) : null}
                </div>
              </Table.Row>
            ))}
          </Table.Body>
        )}
      </Table>
      {records.length === 0 && (
        <div className='text-center'>No Users Found</div>
      )}
      <OSPagination
        numPages={Math.ceil(records.length / recordsPerPage)}
        selectedPage={selectedPage}
        rowsPerPage={recordsPerPage}
        selectedRowsPerPage={[10, 20, 30, 40, 50]}
        onPageSelect={setSelectedPage}
        onSelectRowsChange={setRecordsPerPage}
      />
    </DashboardCard>
  );
}

export default TableUsers;
export { TableUsers };
